import type { Dispatch, FC, ReactNode, SetStateAction } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';

export type ProviderWithValue<T = any> = {
    Provider: FC<{ value: T; children: ReactNode }>;
    defaultValue: T;
};
type ProviderFC<T> = FC<{
    children: ReactNode;
    value: ContextValue<T>;
}>;

export type GenericContextProviderBuilder<T> = (value: T) => ProviderWithValue<T>;

type ContextValue<T> = {
    value: T;
    setValue: Dispatch<SetStateAction<T>>;
    resetValue: () => void;
};
type CreateGenericContextType<T> = {
    useContext: () => ContextValue<T>;
    createContextProvider: GenericContextProviderBuilder<T>;
};

type ReadonlyContextValue<T> = {
    value: T;
};
type CreateReadonlyGenericContextType<T> = {
    useContext: () => ReadonlyContextValue<T>;
    createContextProvider: GenericContextProviderBuilder<T>;
};

type CreateContextOptions = {
    optional?: boolean;
    writable?: boolean;
    // improves errors
    contextName?: string;
};

const getUnsupportedSetValue = (contextName: string) => () => {
    throw new Error(`Setting value to context '${contextName}' is not supported`);
};

const getUnsupportedResetValue = (contextName: string) => () => {
    throw new Error(`Resetting value of context '${contextName}' is not supported`);
};

function getProvider<T>(
    Provider: ProviderFC<T>,
    writable: boolean | undefined,
    contextName: string,
): FC<{ value: T; children: ReactNode }> {
    if (!writable) {
        return ({ value, children }) => (
            <Provider
                value={{
                    value,
                    setValue: getUnsupportedSetValue(contextName),
                    resetValue: getUnsupportedResetValue(contextName),
                }}
            >
                {children}
            </Provider>
        );
    }
    return ({ value, children }) => {
        const [stateValue, setStateValue] = useState(value);
        const resetValue = useCallback(() => setStateValue(value), [value]);
        return (
            <Provider
                value={{
                    value: stateValue,
                    setValue: setStateValue,
                    resetValue,
                }}
            >
                {children}
            </Provider>
        );
    };
}

/**
 * Creates React context and returns builder for provider (so it can be used in multi-context and
 * hook to access to provider value).
 *
 * If the context is not set, error will be thrown upon accessing it using the exposed hook. (unless
 * context was marked as optional).
 *
 * @param opts - options
 */
const buildContext = <T,>(opts?: CreateContextOptions): CreateGenericContextType<T> => {
    const ctxName = opts?.contextName || 'unknown';
    // Create a context with a generic parameter or undefined
    const genericContext = createContext<ContextValue<T | undefined>>({
        value: undefined,
        setValue: getUnsupportedSetValue(ctxName),
        resetValue: getUnsupportedResetValue(ctxName),
    });

    // Check if the value provided to the context is defined or throw an error
    const useGenericContext = (): ContextValue<T> => {
        const contextValue = useContext(genericContext);
        if (opts?.optional) {
            return contextValue as ContextValue<T>;
        }
        if (!contextValue) {
            throw new Error(`Context value '${ctxName}' must be set and within a Provider`);
        }
        return contextValue as ContextValue<T>;
    };

    const createContextProvider = (value: T): ProviderWithValue<T> => ({
        defaultValue: value,
        // we will pretend that provider is just component that accepts value prop
        Provider: getProvider<T>(
            genericContext.Provider as FC<{ value: ContextValue<T> }>,
            opts?.writable,
            ctxName,
        ),
    });

    return { useContext: useGenericContext, createContextProvider };
};
/**
 * Creates an optional generic React context that can be used with or without a provider.
 *
 * @param contextName - Optional custom name for the context (defaults to 'unknown').
 * @returns An object with two methods: `useContext` and `createContextProvider`.
 */
export const createOptionalGenericContext = <T,>(
    contextName?: string,
): CreateGenericContextType<T | undefined> =>
    buildContext<T | undefined>({ optional: true, contextName });

/**
 * Creates a generic React context.
 *
 * @param contextName - Optional custom name for the context (defaults to 'unknown').
 * @returns An object with two methods: `useContext` and `createContextProvider`.
 *
 * @example
 * import { createGenericContext } from '@luna/context';
 *
 * // First we create the context using the helper method
 * const { createContextProvider: createDemoContextProvider, useContext: useDemoContext } = createGenericContext<string>();
 *
 * // Component consuming the data (string)
 * export const DemoComponent: FC = () => {
 *    // we can access the value using the hook; if its not set - error will be thrown by default
 *     const { value: contextValue } = useDemoContext()
 *
 *    return <div>Context value is: {contextValue}</div>
 * }
 */
export const createGenericContext = <T,>(
    contextName?: string,
): CreateReadonlyGenericContextType<T> => buildContext<T>({ contextName });

/**
 * Creates a writable generic React context.
 *
 * A writable context allows consumers to not only read the context value but also
 * modify it using the provided `setValue` function. This is in contrast to read-only
 * contexts where the value can only be set by the provider and not changed by consumers.
 *
 * The context value includes:
 * - `value`: The current context value
 * - `setValue`: A function to update the context value (works like React's setState)
 * - `resetValue`: A function to reset the context value to its initial state
 *
 * @param contextName - Optional custom name for the context (defaults to 'unknown').
 * @returns An object with two methods: `useContext` and `createContextProvider`.
 *
 * @example
 * import { createWritableContext } from '@luna/context';
 * // First we create the context using the helper method
 * const { createContextProvider: createDemoContextProvider, useContext: useDemoContext } = createWritableGenericContext<string>();
 *
 * // Component consuming the data (string)
 * export const DemoComponent: FC = () => {
 *     const { value: contextValue, setValue } = useDemoContext()
 *
 *    // we can set the value using ReactState API
 *    setValue('my new value');
 *
 *    return <div>Context value is: {contextValue}</div>
 * }
 */
export const createWritableGenericContext = <T,>(
    contextName?: string,
): CreateGenericContextType<T> => buildContext<T>({ writable: true, contextName });
